// # Calendar City View Component
// Ultra-zoomed city-level view with enhanced detail and larger cells

'use client';

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, CalendarDays, ZoomIn, ZoomOut } from 'lucide-react';
import type { CalendarEvent, CalendarViewType } from '../../types/calendar';
import { CalendarEventCard } from './CalendarEventCard';
import { CalendarEventChip } from './CalendarEventChip';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';
import { useAuth } from '../../lib/auth';
import { getCurrentLanguage } from '../../lib/language-utils';

interface CalendarCityViewProps {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  onCreateEvent?: (date: Date) => void;
  className?: string;
  showWeekends?: boolean;
  firstDayOfWeek?: number;
}

export function CalendarCityView({
  events,
  onEventClick,
  onDateClick,
  onCreateEvent,
  className = '',
  showWeekends = true,
  firstDayOfWeek = 1
}: CalendarCityViewProps) {
  const { t, i18n } = useTranslation('calendar');
  const { user } = useAuth();
  const userLang = getCurrentLanguage(user?.language_preference);
  const locale = userLang === 'no' ? 'nb-NO' : 'en-US'; // Map language to locale
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1.5); // 150% default zoom (100-200% range)
  const { isMobile } = useDeviceInfo();

  // # Get days for week view (city view shows one week at a time, zoomed in)
  const getDaysToDisplay = useCallback((): Date[] => {
    const days: Date[] = [];
    let startOfWeek = new Date(currentDate);
    
    // Find start of week
    while (startOfWeek.getDay() !== firstDayOfWeek) {
      startOfWeek.setDate(startOfWeek.getDate() - 1);
    }
    
    // Get 7 days
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    
    return days;
  }, [currentDate, firstDayOfWeek]);

  // # Format date helper
  const formatDate = useCallback((date: Date): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return new Date().toISOString().split('T')[0];
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // # Get events for a specific date
  const getEventsForDate = useCallback((date: Date): CalendarEvent[] => {
    if (!events || events.length === 0) return [];
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return [];
    
    const dateStr = formatDate(date);
    
    return events.filter(event => {
      if (!event || !event.start_date || !event.end_date) return false;
      const normalizedStart = event.start_date.split('T')[0];
      const normalizedEnd = event.end_date.split('T')[0];
      return normalizedStart <= dateStr && normalizedEnd >= dateStr;
    });
  }, [events, formatDate]);

  // # Check if date is today
  const isToday = useCallback((date: Date): boolean => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return false;
    }
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  // # Navigation handlers
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // # Zoom controls
  const increaseZoom = () => {
    setZoomLevel(prev => Math.min(2.0, prev + 0.1)); // Max 200%
  };

  const decreaseZoom = () => {
    setZoomLevel(prev => Math.max(1.0, prev - 0.1)); // Min 100%
  };

  // # Get current period label
  const getCurrentPeriodLabel = (): string => {
    const days = getDaysToDisplay();
    const start = days[0];
    const end = days[days.length - 1];
    return `${start.toLocaleDateString(locale, { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // # Get weekday names based on user's locale
  const getWeekdayNames = (): string[] => {
    const baseDate = new Date(2024, 0, 1); // Start with a Monday (Jan 1, 2024)
    const names: string[] = [];
    
    // Generate weekday names from locale
    for (let i = 0; i < 7; i++) {
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + i);
      names.push(date.toLocaleDateString(locale, { weekday: 'short' }));
    }
    
    const reordered = [...names.slice(firstDayOfWeek), ...names.slice(0, firstDayOfWeek)];
    return showWeekends ? reordered : reordered.filter((_, i) => i < 5);
  };

  const days = getDaysToDisplay();
  const weekdayNames = getWeekdayNames();

  // # Calculate zoom styles - using transform with proper containment to avoid scroll interference
  const zoomStyle = {
    transform: `scale(${zoomLevel})`,
    transformOrigin: 'top left',
    width: `${100 / zoomLevel}%`,
    height: `${100 / zoomLevel}%`,
  };

  return (
    <div className={`flex flex-col h-full overflow-hidden ${className}`}>
      {/* Header with navigation and zoom controls */}
      <div className={`flex-shrink-0 border-b border-gray-100 ${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'}`}>
        <div className="flex items-center justify-between mb-2">
          <h2 className={`font-bold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'}`}>
            {getCurrentPeriodLabel()}
          </h2>
          <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-1.5'}`}>
            {!isMobile && (
              <button
                onClick={goToToday}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-all duration-200 flex items-center gap-1.5"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                <span>{t('today')}</span>
              </button>
            )}
            <div className={`flex items-center bg-gray-100 rounded-md ${isMobile ? 'gap-0 p-0.5' : 'gap-0.5 p-0.5'}`}>
              <button
                onClick={goToPrevious}
                className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
                title="Previous week"
              >
                <ChevronLeft className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
              <button
                onClick={goToNext}
                className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
                title="Next week"
              >
                <ChevronRight className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Zoom controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              Zoom: {Math.round(zoomLevel * 100)}%
            </span>
            <div className="flex items-center bg-gray-100 rounded-md gap-0.5 p-0.5">
              <button
                onClick={decreaseZoom}
                disabled={zoomLevel <= 1.0}
                className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'} disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Zoom out"
              >
                <ZoomOut className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
              <button
                onClick={increaseZoom}
                disabled={zoomLevel >= 2.0}
                className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'} disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Zoom in"
              >
                <ZoomIn className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar grid with zoom - prevent scroll-based zoom */}
      <div 
        className="flex-1 overflow-auto"
        onWheel={(e) => {
          // Allow normal scrolling, prevent zoom on Ctrl+Wheel or Cmd+Wheel
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
          }
        }}
        style={{ 
          touchAction: 'pan-y', // Allow vertical pan only, prevent pinch zoom
          userSelect: 'none' // Prevent text selection during scroll
        }}
      >
        <div style={zoomStyle} className={`flex-1 flex flex-col ${isMobile ? 'p-2' : 'p-4'}`}>
          {/* Weekday headers - larger in city view */}
          <div className={`grid grid-cols-7 flex-shrink-0 ${isMobile ? 'mb-2' : 'mb-3'}`}>
            {weekdayNames.map((day) => (
              <div
                key={day}
                className={`text-center font-bold text-gray-700 uppercase ${
                  isMobile ? 'text-sm py-2' : 'text-lg py-3'
                } border-b-2 border-gray-200`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days - much larger cells */}
          <div className={`flex-1 grid grid-cols-7 grid-rows-1 gap-${isMobile ? '2' : '3'}`}>
            {days.map((date, index) => {
              if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
                return null;
              }
              
              const dateEvents = getEventsForDate(date);
              const isTodayDate = isToday(date);
              const isSelected = selectedDate && formatDate(selectedDate) === formatDate(date);

              return (
                <div
                  key={`${date.toISOString()}-${index}`}
                  className={`border-2 rounded-lg cursor-pointer transition-all duration-200 flex flex-col ${
                    isMobile ? 'p-2' : 'p-4'
                  } ${
                    isTodayDate
                      ? 'bg-blue-50 border-blue-300 shadow-lg'
                      : isSelected
                      ? 'bg-blue-50/50 border-blue-200'
                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
                  } min-h-[200px]`}
                  onClick={() => {
                    setSelectedDate(date);
                    onDateClick?.(date);
                  }}
                >
                  {/* Day number - larger */}
                  <div className={`flex items-center justify-between mb-${isMobile ? '2' : '3'}`}>
                    <span
                      className={`font-bold ${
                        isMobile ? 'text-lg' : 'text-2xl'
                      } ${
                        isTodayDate
                          ? `text-blue-700 bg-blue-200 rounded-full flex items-center justify-center ${
                              isMobile ? 'w-8 h-8' : 'w-10 h-10'
                            }`
                          : 'text-gray-900'
                      }`}
                    >
                      {date.getDate()}
                    </span>
                    {dateEvents.length > 0 && (
                      <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
                        isTodayDate 
                          ? 'bg-blue-200 text-blue-700' 
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {dateEvents.length}
                      </span>
                    )}
                  </div>

                  {/* Events - show more in city view */}
                  <div className={`flex-1 space-y-${isMobile ? '1' : '2'} overflow-y-auto`}>
                    {dateEvents.length === 0 ? (
                      <div className="text-center text-gray-400 text-sm py-4">
                        No events
                      </div>
                    ) : (
                      dateEvents.map((event) => (
                        <CalendarEventChip
                          key={event.id}
                          event={event}
                          onClick={(evt) => {
                            onEventClick?.(evt);
                          }}
                          compact={false}
                        />
                      ))
                    )}
                  </div>

                  {/* Quick add button */}
                  {onCreateEvent && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateEvent(date);
                      }}
                      className={`mt-2 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded px-2 py-1 transition-colors ${
                        isMobile ? 'text-[10px]' : ''
                      }`}
                    >
                      + Add event
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CalendarCityView;

